import React, { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { listMemoryLibraryIds, countMemory } from '@/lib/api/memory'
import { DataTable } from '@/components/shadcn/data-table'
import { createMemoryLibrariesColumns, type MemoryLibrary } from './memory-libraries-columns'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/shadcn/pagination'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'

export default function MemoryLibrariesPage() {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const location = useLocation()
  
  // 分页状态
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(10)
  
  // 获取当前语言前缀
  const getCurrentLanguagePrefix = () => {
    const path = location.pathname
    if (path.startsWith('/en')) return '/en'
    if (path.startsWith('/zh')) return '/zh'
    return '/zh' // 默认中文
  }
  
  const languagePrefix = getCurrentLanguagePrefix()

  const { data: libraryIds, isLoading: loadingLibs, error: libErr } = useQuery({
    queryKey: ['memory-library-ids'],
    queryFn: () => listMemoryLibraryIds(),
  })

  // 计算分页数据
  const paginatedLibraryIds = useMemo(() => {
    if (!libraryIds) return []
    const startIndex = (page - 1) * limit
    const endIndex = startIndex + limit
    return libraryIds.slice(startIndex, endIndex)
  }, [libraryIds, page, limit])

  // 只对当前页的库进行计数查询，避免多次API调用
  const { data: counts } = useQuery({
    queryKey: ['memory-library-counts', paginatedLibraryIds],
    enabled: !!paginatedLibraryIds && paginatedLibraryIds.length > 0,
    queryFn: async () => {
      const ids = paginatedLibraryIds || []
      const results = await Promise.all(
        ids.map(async (id) => {
          try {
            const r = await countMemory({ memory_id: id })
            return { id, count: r.count }
          } catch {
            return { id, count: 0 }
          }
        })
      )
      return results
    },
  })

  const countById = useMemo(() => {
    const map = new Map<string, number>()
    ;(counts || []).forEach((c) => map.set(c.id, c.count))
    return map
  }, [counts])

  // 将数据转换为 DataTable 需要的格式
  const tableData = useMemo((): MemoryLibrary[] => {
    if (!paginatedLibraryIds) return []
    return paginatedLibraryIds.map(id => ({
      id,
      count: countById.get(id) ?? 0
    }))
  }, [paginatedLibraryIds, countById])

  // 计算总页数
  const totalPages = useMemo(() => {
    if (!libraryIds) return 0
    return Math.ceil(libraryIds.length / limit)
  }, [libraryIds, limit])

  // 处理行点击事件
  const handleRowClick = (library: MemoryLibrary) => {
    navigate(`${languagePrefix}/dashboard/memories/detail?memory_id=${encodeURIComponent(library.id)}`)
  }



  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-muted-foreground mb-6">
          {loadingLibs ? t('memoryVaults.loading') : libErr ? t('memoryVaults.loadError') : t('memoryVaults.totalVaults', { count: libraryIds?.length || 0 })}
        </p>
        
        {/* 每页数量选择器 */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{t('memoryVaults.itemsPerPage')}</span>
            <Select value={String(limit)} onValueChange={(v) => { setPage(1); setLimit(Number(v)) }}>
              <SelectTrigger className="w-20">
                <SelectValue placeholder={String(limit)} />
              </SelectTrigger>
              <SelectContent>
                {[5, 10, 20, 50, 100].map(n => (
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-4">
          <DataTable columns={createMemoryLibrariesColumns(t)} data={tableData} onRowClick={handleRowClick} />
          
          {/* 分页组件 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <div className="text-sm text-muted-foreground whitespace-nowrap pl-1">
                {t('memoryVaults.pagination.showing', { 
                  start: (page - 1) * limit + 1, 
                  end: Math.min(page * limit, libraryIds?.length || 0),
                  total: libraryIds?.length || 0 
                })}
              </div>
              <Pagination className="justify-end">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        if (page <= 1 || loadingLibs) return
                        setPage((p) => Math.max(1, p - 1))
                      }}
                      className={page <= 1 || loadingLibs ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                  
                  {/* 页码显示 */}
                  {totalPages > 1 && (
                    <>
                      {/* 显示页码 */}
                      {(() => {
                        const pages: number[] = []
                        
                        if (totalPages <= 7) {
                          // 总页数少于等于7页，显示所有页码
                          for (let i = 1; i <= totalPages; i++) {
                            pages.push(i)
                          }
                        } else {
                          // 总页数大于7页，智能显示页码
                          if (page <= 4) {
                            // 当前页在前4页，显示前6页 + 最后一页
                            for (let i = 1; i <= 6; i++) {
                              pages.push(i)
                            }
                            if (totalPages > 6) {
                              pages.push(totalPages)
                            }
                          } else if (page >= totalPages - 3) {
                            // 当前页在后4页，显示第1页 + 后6页
                            pages.push(1)
                            for (let i = totalPages - 5; i <= totalPages; i++) {
                              if (i > 1) pages.push(i)
                            }
                          } else {
                            // 当前页在中间，显示第1页 + 当前页前后2页 + 最后一页
                            pages.push(1)
                            for (let i = page - 2; i <= page + 2; i++) {
                              if (i > 1 && i < totalPages) pages.push(i)
                            }
                            pages.push(totalPages)
                          }
                        }
                        
                        return pages.map((pageNum, index) => {
                          const showEllipsis = index > 0 && pageNum - pages[index - 1] > 1
                          return (
                            <React.Fragment key={pageNum}>
                              {showEllipsis && (
                                <PaginationItem>
                                  <span className="flex h-9 w-9 items-center justify-center">
                                    <span className="sr-only">{t('memoryVaults.pagination.morePages')}</span>
                                    ...
                                  </span>
                                </PaginationItem>
                              )}
                              <PaginationItem>
                                <PaginationLink
                                  href="#"
                                  isActive={pageNum === page}
                                  onClick={(e) => {
                                    e.preventDefault()
                                    if (loadingLibs) return
                                    setPage(pageNum)
                                  }}
                                  className="cursor-pointer"
                                >
                                  {pageNum}
                                </PaginationLink>
                              </PaginationItem>
                            </React.Fragment>
                          )
                        })
                      })()}
                    </>
                  )}
                  
                  <PaginationItem>
                    <PaginationNext 
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        if (page >= totalPages || loadingLibs) return
                        setPage((p) => p + 1)
                      }}
                      className={page >= totalPages || loadingLibs ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}



